import type {
  BehaviorDomain, ConsumptionMethodRecord, HabitCategory, HabitRecord, MethodLogMode,
} from '../core/types.js';
import { uuid } from '../core/ids.js';
import { nowIso } from '../core/time.js';

interface MethodSeed {
  kind: string;
  label: string;
  unitSingular: string;
  unitPlural: string;
  defaultLogMode: MethodLogMode;
  defaultSessionMinutes?: number;
  mergeWindowMinutes?: number;
  defaultDomainUnits: number;
  sortOrder: number;
}

export const NICOTINE_DEFAULT_METHODS: readonly MethodSeed[] = [
  {
    kind: 'cigarette',
    label: 'Cigarette',
    unitSingular: 'cigarette',
    unitPlural: 'cigarettes',
    defaultLogMode: 'instant',
    defaultDomainUnits: 1,
    sortOrder: 10,
  },
  {
    kind: 'vape',
    label: 'Vape',
    unitSingular: 'vape session',
    unitPlural: 'vape sessions',
    defaultLogMode: 'session',
    defaultSessionMinutes: 5,
    mergeWindowMinutes: 10,
    defaultDomainUnits: 1,
    sortOrder: 20,
  },
  {
    kind: 'pouch',
    label: 'Pouch',
    unitSingular: 'pouch session',
    unitPlural: 'pouch sessions',
    defaultLogMode: 'extended_session',
    defaultSessionMinutes: 45,
    mergeWindowMinutes: 45,
    defaultDomainUnits: 1,
    sortOrder: 30,
  },
  {
    kind: 'dip',
    label: 'Dip',
    unitSingular: 'dip session',
    unitPlural: 'dip sessions',
    defaultLogMode: 'extended_session',
    defaultSessionMinutes: 45,
    mergeWindowMinutes: 45,
    defaultDomainUnits: 1,
    sortOrder: 40,
  },
];

export const ALCOHOL_DEFAULT_METHODS: readonly MethodSeed[] = [
  {
    kind: 'beer',
    label: 'Beer',
    unitSingular: 'beer',
    unitPlural: 'beers',
    defaultLogMode: 'instant',
    defaultDomainUnits: 1,
    sortOrder: 10,
  },
  {
    kind: 'wine',
    label: 'Wine',
    unitSingular: 'glass',
    unitPlural: 'glasses',
    defaultLogMode: 'instant',
    defaultDomainUnits: 1,
    sortOrder: 20,
  },
  {
    kind: 'liquor',
    label: 'Liquor',
    unitSingular: 'drink',
    unitPlural: 'drinks',
    defaultLogMode: 'instant',
    defaultDomainUnits: 1,
    sortOrder: 30,
  },
  {
    kind: 'mixed',
    label: 'Mixed drink',
    unitSingular: 'mixed drink',
    unitPlural: 'mixed drinks',
    defaultLogMode: 'instant',
    defaultDomainUnits: 1,
    sortOrder: 40,
  },
];

export const DEFAULT_METHODS_BY_DOMAIN: Partial<Record<BehaviorDomain, readonly MethodSeed[]>> = {
  nicotine: NICOTINE_DEFAULT_METHODS,
  alcohol: ALCOHOL_DEFAULT_METHODS,
};

export const CATEGORY_TO_DOMAIN: Record<HabitCategory, BehaviorDomain> = {
  smoking: 'nicotine',
  vaping: 'nicotine',
  alcohol: 'alcohol',
  cannabis: 'cannabis',
  caffeine: 'caffeine',
  social_media: 'digital',
  gaming: 'digital',
  gambling: 'gambling',
  porn: 'other',
  shopping: 'shopping',
  snacking: 'food',
  other: 'other',
};

export const CATEGORY_TO_DEFAULT_METHOD_KIND: Record<HabitCategory, string> = {
  smoking: 'cigarette',
  vaping: 'vape',
  alcohol: 'drink',
  cannabis: 'session',
  caffeine: 'serving',
  social_media: 'session',
  gaming: 'session',
  gambling: 'session',
  porn: 'session',
  shopping: 'purchase',
  snacking: 'snack',
  other: 'use',
};

export function buildMethodFromSeed(
  habit: Pick<HabitRecord, 'id'>,
  domain: BehaviorDomain,
  seed: MethodSeed,
): ConsumptionMethodRecord {
  const now = nowIso();
  const rec: ConsumptionMethodRecord = {
    id: uuid(),
    schemaVersion: 2,
    habitId: habit.id,
    domain,
    kind: seed.kind,
    label: seed.label,
    unitSingular: seed.unitSingular,
    unitPlural: seed.unitPlural,
    defaultLogMode: seed.defaultLogMode,
    defaultDomainUnits: seed.defaultDomainUnits,
    active: true,
    sortOrder: seed.sortOrder,
    createdAt: now,
    updatedAt: now,
  };
  if (seed.defaultSessionMinutes !== undefined) rec.defaultSessionMinutes = seed.defaultSessionMinutes;
  if (seed.mergeWindowMinutes !== undefined) rec.mergeWindowMinutes = seed.mergeWindowMinutes;
  return rec;
}

/**
 * Build a single default method for a legacy habit that has no methods yet.
 * Uses category mapping when known; otherwise falls back to the habit's unit labels.
 */
export function defaultMethodForLegacyHabit(habit: HabitRecord): ConsumptionMethodRecord {
  const domain: BehaviorDomain = habit.domain ?? CATEGORY_TO_DOMAIN[habit.category] ?? 'other';
  const seedFromCategory: MethodSeed | undefined = (() => {
    const seeds = DEFAULT_METHODS_BY_DOMAIN[domain];
    if (!seeds) return undefined;
    const wantKind = CATEGORY_TO_DEFAULT_METHOD_KIND[habit.category];
    return seeds.find((s) => s.kind === wantKind) ?? seeds[0];
  })();
  if (seedFromCategory) return buildMethodFromSeed(habit, domain, seedFromCategory);
  const fallbackKind = CATEGORY_TO_DEFAULT_METHOD_KIND[habit.category] ?? 'use';
  return buildMethodFromSeed(habit, domain, {
    kind: fallbackKind,
    label: habit.label,
    unitSingular: habit.unitSingular,
    unitPlural: habit.unitPlural,
    defaultLogMode: 'instant',
    defaultDomainUnits: 1,
    sortOrder: 10,
  });
}
